'use client'

import React, { useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

export default function ContactForm() {
  const [state, setState] = useState<'closed' | 'form' | 'success' | 'error'>('closed')
  const [errors, setErrors] = useState<{ message: string; errorType: string }[]>([])

  const handleClickOpen = () => {
    setState('form')
  }

  const handleClose = () => {
    setState('closed')
  }

  return (
    <>
      <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={handleClickOpen}>
        Get in touch
      </Link>
      <Dialog
        fullWidth
        maxWidth="md"
        open={state !== 'closed'}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: 'form',
            async onSubmit(event: React.SubmitEvent<HTMLElement>) {
              event.preventDefault()

              // https://developers.hubspot.com/docs/reference/api/marketing/forms/v3-legacy#submit-data-to-a-form-unauthenticated
              const formData = new FormData(event.target)
              const payload = {
                fields: formData
                  .entries()
                  .toArray()
                  .filter(([name]) => name !== 'consentToProcess' && name !== 'communications')
                  .map(([name, value]) => ({ name, value })),
                context: {
                  pageUri: 'https://www.isdacreate.org',
                  pageName: 'ISDA Create | Powered by CreateiQ',
                },
                legalConstentOptions: {
                  consent: {
                    // Include this object when GDPR options are enabled
                    consentToProcess: !!formData.get('consentToProcess'),
                    text: 'I agree to allow CreateiQ to store and process my personal data.',
                    communications: [
                      {
                        value: !!formData.get('communications'),
                        subscriptionTypeId: 999,
                        text: 'I agree to receive marketing communications from CreateiQ.',
                      },
                    ],
                  },
                },
              }

              try {
                const response = await fetch(
                  'https://api.hsforms.com/submissions/v3/integration/submit/6096229/8266e6a4-7fd4-4328-a0f7-59d0e6fa66c1',
                  {
                    method: 'POST',
                    headers: {
                      'Content-type': 'application/json;charset=utf-8',
                    },
                    mode: 'cors',
                    body: JSON.stringify(payload),
                  }
                )

                const result = await response.json<{
                  redirectUri?: string
                  inlineMessage?: string
                  errors?: { message: string; errorType: string }[]
                }>()

                if (result.errors && result.errors.length > 0) {
                  setErrors(result.errors)
                  setState('error')
                  return
                }

                setState('success')
              } catch {
                setErrors([])
                setState('error')
              }
            },
          },
        }}
      >
        <DialogTitle>Get in touch</DialogTitle>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={theme => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {state === 'form' && (
            <>
              <TextField
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                required
                margin="normal"
                name="firstname"
                label="First name"
                placeholder="John"
                type="text"
                fullWidth
                variant="filled"
                sx={{ marginTop: 0 }}
              />
              <TextField
                required
                margin="normal"
                name="lastname"
                label="Last name"
                placeholder="Smith"
                type="text"
                fullWidth
                variant="filled"
              />
              <TextField
                required
                margin="normal"
                name="company"
                label="Company"
                placeholder="My Company"
                type="text"
                fullWidth
                variant="filled"
              />
              <TextField
                margin="normal"
                name="jobtitle"
                label="Job title"
                placeholder="Account Manager"
                type="text"
                fullWidth
                variant="filled"
              />
              <TextField
                margin="normal"
                name="phone"
                label="Phone Number"
                placeholder="+44 (0) 000 000 00 00"
                type="tel"
                fullWidth
                variant="filled"
              />
              <TextField
                required
                margin="normal"
                name="email"
                label="Work E-mail"
                placeholder="john.smith@email.com"
                type="email"
                fullWidth
                variant="filled"
              />
              <FormControl margin="normal" fullWidth variant="filled">
                <InputLabel id="hearaboutus-label">How did you hear about us?</InputLabel>
                <Select labelId="hearaboutus-label" id="hearaboutus" label="How did you hear about us?">
                  <MenuItem disabled selected value="">
                    <em>Please select one:</em>
                  </MenuItem>
                  <MenuItem value="friend_or_colleague">Friend/Colleague</MenuItem>
                  <MenuItem value="social_media">Social Media</MenuItem>
                  <MenuItem value="conference">Conference</MenuItem>
                  <MenuItem value="search_engine">Search Engine</MenuItem>
                  <MenuItem value="press">Press</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormGroup sx={{ my: 1 }}>
                <FormControlLabel
                  required
                  name="consentToProcess"
                  control={<Checkbox />}
                  label="I agree to allow CreateiQ to store and process my personal data."
                />
              </FormGroup>
              <FormGroup sx={{ my: 1 }}>
                <FormControlLabel
                  name="communications"
                  control={<Checkbox />}
                  label="I agree to receive marketing communications from CreateiQ."
                />
              </FormGroup>
            </>
          )}
          {state === 'success' && (
            <>
              <Typography variant="body1" color="primary" sx={{ mb: 1.25 }}>
                Thank you for your request!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1.25 }}>
                An expert will be in touch with you
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You can now close this window
              </Typography>
            </>
          )}
          {state === 'error' && (
            <>
              <Typography variant="body1" color="primary" sx={{ mb: 1.25 }}>
                There was a problem with your submission:
              </Typography>
              {errors.map(({ errorType, message }) => (
                <Typography key={errorType} variant="body1" color="text.secondary" sx={{ mb: 1.25 }}>
                  {errorType === 'INVALID_EMAIL' ? 'The email address provided was invalid.' : message}
                </Typography>
              ))}
              <Typography variant="body1" color="text.secondary">
                If the problem persists, please get in touch with us at{' '}
                <Link href="mailto:hello@createiq.tech?subject=Talk+to+an+expert">hello@isdacreate.org</Link>
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ marginTop: 0, paddingLeft: 3, paddingRight: 3, paddingBottom: 2.5 }}>
          {state === 'form' && (
            <Button type="submit" variant="contained" color="info" fullWidth>
              Submit
            </Button>
          )}
          {state === 'success' && (
            <Button type="button" variant="contained" color="info" fullWidth onClick={handleClose}>
              Close
            </Button>
          )}
          {state === 'error' && (
            <Button
              type="button"
              variant="contained"
              color="info"
              fullWidth
              onClick={() => {
                setState('form')
              }}
            >
              Try again
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
